import React, { useEffect, useMemo, useState } from 'react';

import AlertBanner from '~/components/core/AlertBanner';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import { CloseIcon_Deprecated } from '~/components/icons';
import ExpandCollapseAllButtons from '~/components/Notes/ExpandCollapseAllButtons';
import Checkbox from '~/components/ui/atoms/Checkbox';
import Switch from '~/components/ui/atoms/Switch';
import type { PromotionPreviewResult } from '~/server_shared/openapi/data-contracts';

import BlockedSectionGroup from './BlockedSectionGroup';
import ChangeListByType from './ChangeListByType';
import { computeDependencyLocks, computeMetabaseRequirements } from './metabaseDependencies';
import { computeNewMemberLocks, seedSelectionWithLocks } from './newMembers';
import type { ScopeDimension, ScopeFilterSets } from './promotionScope';
import {
  distinctScopeValues,
  GLOBAL_SCOPE_VALUE,
  hasGlobalInDimension,
  isAnyScopeFilterActive,
  isChangeVisible,
  SCOPE_DIMENSIONS,
} from './promotionScope';
import { hasCollision, hasPrunedReferences } from './promotionsUtils';
import type { PromotionBlockedSection, PromotionChange } from './types';
import { useScopeValueLabel } from './useScopeValueLabel';

// Empty facet selections — the default (no filter) and the reset applied when a new preview arrives.
const emptyFilters = (): Record<ScopeDimension, string[]> => ({ sub_organizations: [], lobs: [] });

// Stable empty default for the promoted-member id props, so the no-new-member case never locks a row.
const EMPTY_MEMBER_IDS: Set<string> = new Set();

// Stable empty default for `blockedSections` — no section's export is blocked.
const NO_BLOCKED_SECTIONS: PromotionBlockedSection[] = [];

interface PromotionChangeSelectorProps {
  /** The previewed diff to select from. */
  previewResult: PromotionPreviewResult;
  /** Reports the current selection up so the parent dialog can drive the footer submit action. Must be a
   * stable reference (e.g. a `useState` setter). */
  onSelectionChange: (selectedChangeIds: string[]) => void;
  /** Reports whether submit must be blocked: true when one or more *selected* changes are currently hidden by
   * the active scope filters (so they can't be reviewed). The parent disables Submit while this is true.
   * Must be a stable reference. */
  onSubmitBlockedChange?: (blocked: boolean) => void;
  /** Parent-supplied content rendered above the change list (e.g. a pending/error banner). */
  banner?: React.ReactNode;
  /** Shown when the preview carries no changes. */
  emptyMessage?: string;
  /** The promoted new-member ids from the interim step. Their member-defining CREATE rows are locked to
   * this decision: a promoted member's CREATE is force-selected and disabled, an opted-out member's is
   * force-deselected and disabled — so a reference can't be kept while its member is dropped. Empty (the
   * default) → no rows locked, i.e. the common no-new-member promotion behaves exactly as before. */
  promotedLobIds?: Set<string>;
  promotedSubOrgIds?: Set<string>;
  /** Sections left out of the preview because one side's export of them is blocked, shown disabled after the
   * change list (or beneath the empty state). Owned by the parent: the initial preview carries them and a
   * refine does not re-send them. */
  blockedSections?: PromotionBlockedSection[];
}

/** Shared change-selection body for the promotion create flows (live source + uploaded artifact): owns the
 * selection + collapse state, renders the collision banner, the select-all / "N of M selected" counter row,
 * the two scope facet filters + "Show scopes" toggle, and the per-type change list. It reports the selection
 * up via `onSelectionChange` and (when a filter hides selected changes) blocks submit via
 * `onSubmitBlockedChange`; the parent dialog owns the Preview/Submit actions (in the dialog footer, beside
 * Cancel) — how the preview is produced (a live pairing preview or a multipart upload) and what submit does
 * (POST to the pairing route or the upload route). */
const PromotionChangeSelector: React.FC<PromotionChangeSelectorProps> = ({
  previewResult,
  onSelectionChange,
  onSubmitBlockedChange,
  banner,
  emptyMessage = 'The target is already up to date — nothing to promote.',
  promotedLobIds = EMPTY_MEMBER_IDS,
  promotedSubOrgIds = EMPTY_MEMBER_IDS,
  blockedSections = NO_BLOCKED_SECTIONS,
}) => {
  const changes = useMemo(() => (previewResult.changes ?? []) as PromotionChange[], [previewResult.changes]);
  // New-member CREATE rows are locked to the interim step's promote/opt-out decision (see `newMembers`): a
  // promoted member's CREATE stays checked + disabled, an opted-out member's is unchecked + disabled. Empty
  // promoted sets (the default / no new members) yield no locks, so nothing below changes for that case.
  const newMemberLocks = useMemo(
    () => computeNewMemberLocks(changes, previewResult.new_members, promotedLobIds, promotedSubOrgIds),
    [changes, previewResult.new_members, promotedLobIds, promotedSubOrgIds]
  );
  const { lockedChangeIds } = newMemberLocks;
  // Which pending Metabase CREATEs each Metabase change needs on the target (see `metabaseDependencies`).
  const requirementGraph = useMemo(() => computeMetabaseRequirements(changes), [changes]);

  // Display names for facet options / selected values — shared with the per-change scope rows.
  const scopeValueLabel = useScopeValueLabel();

  // Per-facet options: the distinct member values in each dimension, prefixed with the "Global" sentinel when
  // some change is unscoped there (so a user can narrow to global-only). A dimension with no member values
  // contributes no facet (nothing to filter by).
  const facetOptions = useMemo(() => {
    const result = emptyFilters();
    SCOPE_DIMENSIONS.forEach(({ key }) => {
      const values = distinctScopeValues(changes, key);
      result[key] = values.length > 0 && hasGlobalInDimension(changes, key) ? [GLOBAL_SCOPE_VALUE, ...values] : values;
    });
    return result;
  }, [changes]);
  // Whether any scope UI is meaningful at all (some change carries scope). Gates both facet filters and
  // the per-change scope rows — a fully-unscoped preview shows neither.
  const hasAnyScopeOptions = SCOPE_DIMENSIONS.some(({ key }) => facetOptions[key].length > 0);

  // A fresh preview selects everything by default (selecting all == a full promotion). Seeded synchronously
  // so the first paint already shows the full selection / correct count rather than a post-effect flash of
  // "0 of N".
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() =>
    seedSelectionWithLocks(
      changes.map((c) => c.change_id),
      newMemberLocks
    )
  );
  // Config-type sections whose change-rows are collapsed (empty by default → all expanded). Folding a long
  // change list must not drop the selection, which lives here rather than in the mounted rows.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // Active facet-filter selections (member values incl. the "Global" sentinel), keyed by dimension. Empty
  // arrays = no filter (the default). Filtering only narrows the view + select-all; the global selection
  // submit contract is unaffected — a hidden row keeps its selection (surfaced via `onSubmitBlockedChange`).
  const [filters, setFilters] = useState<Record<ScopeDimension, string[]>>(emptyFilters);
  // "Show scopes" toggle (default on). Deliberately NOT reset per preview.
  const [showScopes, setShowScopes] = useState(true);

  // Reset the selection + collapse + filter state during render (not in an effect) when a new preview arrives,
  // so the first paint of the new preview already reflects "all selected" — never a stale count carried over
  // from the previous preview. Keyed on the preview identity.
  const [seenPreview, setSeenPreview] = useState(previewResult);
  if (seenPreview !== previewResult) {
    setSeenPreview(previewResult);
    setSelectedIds(
      seedSelectionWithLocks(
        changes.map((c) => c.change_id),
        newMemberLocks
      )
    );
    setCollapsedGroups(new Set());
    setFilters(emptyFilters());
  }

  // `selectedIds` holds only the user's own choices. The selection that counts adds every pending Metabase
  // CREATE a chosen item needs, and those rows stay locked while it is chosen. Deriving them rather than
  // storing them means unchecking the item hands each row back to the user's own choice, and a bulk uncheck
  // leaves nothing behind.
  const dependencyLocks = useMemo(
    () => computeDependencyLocks(requirementGraph, selectedIds, changes),
    [requirementGraph, selectedIds, changes]
  );
  const effectiveSelectedIds = useMemo(
    () => new Set([...selectedIds, ...dependencyLocks.requiredIds]),
    [selectedIds, dependencyLocks]
  );

  // Report the selection up so the parent can enable/disable and drive its footer Submit action.
  useEffect(() => {
    onSelectionChange(Array.from(effectiveSelectedIds));
  }, [effectiveSelectedIds, onSelectionChange]);

  const selectedChanges = changes.filter((c) => effectiveSelectedIds.has(c.change_id));

  const filterSets = useMemo<ScopeFilterSets>(
    () => ({ sub_organizations: new Set(filters.sub_organizations), lobs: new Set(filters.lobs) }),
    [filters]
  );
  const filterActive = isAnyScopeFilterActive(filterSets);

  // The changes shown + governed by the toolbar (select-all, counter, expand/collapse) under the active scope
  // filters. No active filter → all changes (byte-identical to the unfiltered view). Otherwise a change is
  // visible when it matches every facet (OR within a facet, AND across facets; see `isChangeVisible`).
  const visibleChanges = useMemo(
    () => (filterActive ? changes.filter((change) => isChangeVisible(change, filterSets)) : changes),
    [changes, filterActive, filterSets]
  );

  const handleToggleChanges = (ids: string[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        // Locked new-member rows are pinned to the interim step's decision — never toggled from here.
        if (lockedChangeIds.has(id)) {
          return;
        }
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const visibleChangeIds = visibleChanges.map((c) => c.change_id);
  const selectedVisibleCount = visibleChangeIds.filter((id) => effectiveSelectedIds.has(id)).length;
  // The select-all checkbox governs only the toggleable (non-locked) rows, so a force-deselected opted-out
  // member doesn't keep it perpetually indeterminate. With no locks this equals the full visible set. A row a
  // selected Metabase item requires stays in it, so unchecking select-all releases that row too.
  const toggleableVisibleIds = visibleChangeIds.filter((id) => !lockedChangeIds.has(id));
  const selectedToggleableCount = toggleableVisibleIds.filter((id) => effectiveSelectedIds.has(id)).length;
  const allSelected = toggleableVisibleIds.length > 0 && selectedToggleableCount === toggleableVisibleIds.length;
  const someSelected = selectedToggleableCount > 0 && !allSelected;
  // Selected rows hidden by the active filters. Submit promotes the whole selection, not just the visible
  // subset, so a hidden selection would be promoted silently — instead we block submit and surface it
  // (see the inline banner + `onSubmitBlockedChange`). 0 when no filter is active (every change is visible).
  const hiddenSelectedCount = effectiveSelectedIds.size - selectedVisibleCount;
  const submitBlocked = hiddenSelectedCount > 0;

  // "Show scopes" is forced on (and its toggle disabled) whenever a filter is active — the per-line scope is
  // the "why is this shown" explanation, so it can't be dismissed while filtering.
  const effectiveShowScopes = hasAnyScopeOptions && (filterActive || showScopes);

  // Block the parent's Submit while any selected change is hidden by the filters.
  useEffect(() => {
    onSubmitBlockedChange?.(submitBlocked);
  }, [submitBlocked, onSubmitBlockedChange]);

  const handleToggleGroupCollapsed = (configurationType: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(configurationType)) {
        next.delete(configurationType);
      } else {
        next.add(configurationType);
      }
      return next;
    });
  };

  // Expand-all clears the collapsed set; collapse-all fills it with every currently-visible group key.
  const handleExpandCollapseAll = (shouldExpandAll: boolean) => {
    setCollapsedGroups(shouldExpandAll ? new Set() : new Set(visibleChanges.map((c) => c.configuration_type)));
  };

  return (
    <div className="flex flex-col gap-16">
      {banner}

      {hasCollision(selectedChanges) && (
        <AlertBanner
          withIcon
          alertType={AlertBanner.ALERT_TYPES.WARNING}
          title="Collision detected"
          note="One or more selected changes conflict with the target's current configuration. Promoting will overwrite the target's values."
        />
      )}

      {hasPrunedReferences(selectedChanges) && (
        <AlertBanner
          withIcon
          alertType={AlertBanner.ALERT_TYPES.WARNING}
          title="References pruned"
          note="One or more selected changes reference LOBs or sub-organizations that don't exist on the target. Those references were pruned from the promoted configuration so it applies cleanly; a change left with none is dropped."
        />
      )}

      {changes.length === 0 ? (
        <>
          <div className="text-slate-700">{emptyMessage}</div>
          {blockedSections.map((section) => (
            <BlockedSectionGroup key={`${section.side}:${section.configuration_type}`} section={section} />
          ))}
        </>
      ) : (
        <>
          <div className="flex flex-col gap-y-8">
            {hasAnyScopeOptions && (
              <div className="flex flex-wrap items-center justify-between gap-x-16 gap-y-8">
                <div className="flex flex-wrap items-center gap-x-16 gap-y-8">
                  {SCOPE_DIMENSIONS.map(({ key, filterLabel }) =>
                    facetOptions[key].length > 0 ? (
                      <div key={key} className="w-[200px]">
                        <MultiSelectField<string>
                          id={`promotion-scope-filter-${key}`}
                          label={filterLabel}
                          value={filters[key]}
                          options={facetOptions[key]}
                          fullWidth
                          autoSelectSingleOption={false}
                          renderOption={(value) => scopeValueLabel(key, value)}
                          renderValue={(values) => values.map((value) => scopeValueLabel(key, value)).join(', ')}
                          onChange={(values) => setFilters((prev) => ({ ...prev, [key]: values }))}
                          InputProps={{
                            endAdornment:
                              filters[key].length === 0 ? null : (
                                <InputAdornment className="mr-24" position="start">
                                  <IconButton
                                    className="p-8"
                                    aria-label={`Clear ${filterLabel} filter`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFilters((prev) => ({ ...prev, [key]: [] }));
                                    }}
                                  >
                                    <CloseIcon_Deprecated size={10} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                          }}
                        />
                      </div>
                    ) : null
                  )}
                </div>
                <div className="flex items-center gap-x-4 text-slate-700">
                  <Switch
                    aria-label="Show scopes"
                    checked={effectiveShowScopes}
                    disabled={filterActive}
                    onChange={(_e, checked) => setShowScopes(checked)}
                  />
                  <span>Show scopes</span>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-x-16 gap-y-8">
              <div className="flex items-center gap-x-16">
                <Checkbox
                  size="xs"
                  label="Select all"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(_e, checked) => handleToggleChanges(toggleableVisibleIds, checked)}
                />
                <ExpandCollapseAllButtons setForceExpandCollapse={handleExpandCollapseAll} />
              </div>
              <span className="text-slate-700">
                {filterActive
                  ? `Showing ${visibleChanges.length} of ${changes.length} · ${effectiveSelectedIds.size} of ${changes.length} selected`
                  : `${effectiveSelectedIds.size} of ${changes.length} selected`}
              </span>
            </div>
          </div>

          {submitBlocked && (
            <AlertBanner
              withIcon
              alertType={AlertBanner.ALERT_TYPES.WARNING}
              title="Selected changes are hidden by the active filters"
              note={`${hiddenSelectedCount} selected ${
                hiddenSelectedCount === 1 ? 'change is' : 'changes are'
              } hidden by the current scope filters and can't be reviewed. Clear or adjust the filters, or deselect the hidden changes, before submitting.`}
            />
          )}

          <ChangeListByType
            changes={visibleChanges}
            summary={previewResult.summary}
            selectedChangeIds={effectiveSelectedIds}
            lockedChangeIds={lockedChangeIds}
            requiredBy={dependencyLocks.requiredBy}
            onToggleChanges={handleToggleChanges}
            collapsedGroups={collapsedGroups}
            onToggleGroupCollapsed={handleToggleGroupCollapsed}
            showScopes={effectiveShowScopes}
            scopeHighlight={filterSets}
            showPrunedReferences
            blockedSections={blockedSections}
          />
        </>
      )}
    </div>
  );
};

export default PromotionChangeSelector;
